import { ArrowLeftOutlined } from "@ant-design/icons";
import { Avatar, Breadcrumb, Button, Empty, Flex, Input, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux-hooks";
import { type ComplianceCoachingRow, useGetComplianceCliniciansQuery } from "../../service/compliance.service";
import { getRandomAvatarColor } from "../../utils/ui.utils";

const trendColor: Record<string, string> = {
  declining: "#b91c1c",
  stable: "#6b7280",
  improving: "#047857",
};

const CliniciansList = () => {
  const navigate = useNavigate();
  const { userInfo } = useAppSelector((state) => state.auth);
  const [sort, setSort] = useState<string>("issue_count");
  const [search, setSearch] = useState<string>("");

  const { data, isFetching } = useGetComplianceCliniciansQuery({ sort, search, days: 30 });

  const rows = data?.clinicians ?? [];

  const columns: ColumnsType<ComplianceCoachingRow> = useMemo(
    () => [
      {
        title: "Clinician",
        dataIndex: "display_name",
        key: "clinician",
        sorter: true,
        render: (_, row) => (
          <Flex align="center" gap={12}>
            <Avatar style={{ backgroundColor: getRandomAvatarColor(row.display_name) }}>{row.avatar_initials}</Avatar>
            <Flex vertical>
              <Typography.Text strong>{row.display_name}</Typography.Text>
              {row.specialty ? (
                <Typography.Text style={{ fontSize: 11, color: "#6b7280" }}>{row.specialty}</Typography.Text>
              ) : null}
            </Flex>
          </Flex>
        ),
      },
      {
        title: "Notes reviewed",
        dataIndex: "notes_reviewed",
        key: "notes_reviewed",
        width: 140,
        render: (value: number | undefined) => (
          <Typography.Text>{typeof value === "number" ? value : 0}</Typography.Text>
        ),
      },
      {
        title: "Capture rate",
        dataIndex: "capture_rate",
        key: "capture_rate",
        width: 140,
        sorter: true,
        render: (value: number | undefined) =>
          typeof value === "number" ? (
            <Typography.Text strong>{value}%</Typography.Text>
          ) : (
            <Typography.Text style={{ color: "#9ca3af" }}>—</Typography.Text>
          ),
      },
      {
        title: "Issue count",
        dataIndex: "issue_count",
        key: "issue_count",
        width: 130,
        sorter: true,
        render: (value: number | undefined) =>
          typeof value === "number" ? (
            <Typography.Text strong>{value}</Typography.Text>
          ) : (
            <Typography.Text style={{ color: "#9ca3af" }}>—</Typography.Text>
          ),
      },
      {
        title: "Trend",
        dataIndex: "trend_label",
        key: "trend_label",
        width: 130,
        render: (value: string, row) =>
          value ? (
            <Typography.Text style={{ color: trendColor[value] || "#6b7280", fontWeight: 500 }}>
              {value}
              {typeof row.trend_delta_pts === "number"
                ? ` ${row.trend_delta_pts > 0 ? "+" : ""}${row.trend_delta_pts}`
                : ""}
            </Typography.Text>
          ) : (
            <Typography.Text style={{ color: "#9ca3af" }}>—</Typography.Text>
          ),
      },
      {
        title: "Top categories",
        dataIndex: "top_issue_categories",
        key: "top_issue_categories",
        render: (cats: string[]) =>
          cats?.length ? (
            <Flex gap={6} wrap>
              {cats.map((cat) => (
                <Tag key={cat}>{cat}</Tag>
              ))}
            </Flex>
          ) : (
            <Typography.Text style={{ color: "#9ca3af" }}>—</Typography.Text>
          ),
      },
    ],
    [],
  );

  if (!userInfo?.is_compliance_enabled || !userInfo?.is_group_admin) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh", textAlign: "left", padding: "40px" }}>
      <Flex align="center" gap={8} style={{ marginBottom: 20 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/compliance")}
          size="small"
          style={{ color: "#6b7280" }}
        />
        <Breadcrumb
          items={[
            {
              title: (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/compliance");
                  }}
                >
                  Compliance
                </a>
              ),
            },
            { title: "Clinicians" },
          ]}
        />
      </Flex>

      <Flex justify="space-between" align="flex-start" style={{ marginBottom: 24 }}>
        <Flex vertical gap={4}>
          <div
            style={{
              marginBottom: 8,
              color: "#262626",
              fontWeight: 600,
              fontSize: 24,
              lineHeight: "29px",
              letterSpacing: "0.01em",
            }}
          >
            Clinicians
          </div>
          <div style={{ fontWeight: 500, fontSize: 14, lineHeight: "140%", color: "#6A7077" }}>
            Browse every clinician in your Group. Click a row for compliance details, notes, and transcripts.
          </div>
        </Flex>
      </Flex>

      <Flex gap={12} style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Filter by name or email"
          allowClear
          style={{ maxWidth: 360 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>

      <Table
        columns={columns}
        dataSource={rows}
        rowKey="clinician_id"
        loading={isFetching}
        onChange={(_pag, _filters, sorter) => {
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          if (s && s.field && s.order) {
            const map: Record<string, string> = {
              clinician: "name",
              capture_rate: "capture_rate",
              issue_count: "issue_count",
            };
            setSort(map[String(s.field)] || "issue_count");
          }
        }}
        onRow={(row) => ({
          onClick: () => navigate(`/compliance/clinicians/${row.clinician_id}`),
          style: { cursor: "pointer" },
        })}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        locale={{ emptyText: <Empty description="No clinicians in this Group" /> }}
      />
    </div>
  );
};

export default CliniciansList;
